import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  s, authHeaders, Toast, newRecordButtonStyle, ExcelIcon, exportToExcel,
  toGridSortField, toODataSortField,
} from './listPageStyles';
import { ListPageShell, Pagination, StatusBadge, ImportWizard } from './ui';
import {
  useC360List, C360Table, ColumnChooser, buildSelectFields,
  ViewIcon, EditIcon, DeleteIcon, UploadIcon,
} from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/customeradditionalfields';

const DATA_TYPES = [
  { value: 1, label: 'Metin' },
  { value: 2, label: 'Sayı' },
  { value: 3, label: 'Tarih' },
  { value: 4, label: 'Evet/Hayır' },
];
const dataTypeLabel = (v) => DATA_TYPES.find((t) => t.value === Number(v))?.label || '-';

const REQUIRED_OPTIONS = [{ value: 'true', label: 'Evet' }, { value: 'false', label: 'Hayır' }];

const COLUMN_FILTER_META = {
  textId: { type: 'text', odataField: 'TextId' },
  name: { type: 'text', odataField: 'Name' },
  dataType: { type: 'select', odataField: 'DataType', options: DATA_TYPES },
  isRequired: { type: 'select', odataField: 'IsRequired', valueFormat: 'raw', options: REQUIRED_OPTIONS },
};
const SEARCH_FIELDS = ['TextId', 'Name'];

export function CustomerAdditionalFieldsPage() {
  usePageMeta('Müşteri Özelleştirilmiş Alan', 'Müşteriler için özel alan tanımları');
  const navigate = useNavigate();
  const { canAdd, canEdit, canDelete } = usePageActions();

  const [selected, setSelected] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [toast, setToast] = useState(null);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [importOpen, setImportOpen] = useState(false);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  // $select: yalnız görünür kolonların OData alanları + kritikler. CreatedDate/UpdatedDate
  // kolonları COLUMN_FILTER_META'da yok (filtresiz) ama listede row'dan okunuyor → always'e ekli.
  // sortField $select'e katılmaz: $orderby projeksiyondan önce tam entity'ye uygulanır (güvenli)
  // ve sıralama yalnız görünür/always kolonlardan tetiklenir.
  const selectFields = useMemo(() => buildSelectFields({
    columnFilterMeta: COLUMN_FILTER_META,
    columnVisibility,
    always: ['Id', 'CreatedDate', 'UpdatedDate'],
  }), [columnVisibility]);

  const list = useC360List({
    apiBase: API_BASE,
    columnFilterMeta: COLUMN_FILTER_META,
    searchFields: SEARCH_FIELDS,
    defaultSortField: 'TextId',
    selectFields,
    onError: (msg) => showToast('error', msg),
  });

  const {
    rows, totalCount, loading,
    filterInput, setFilterInput, filterText, applyFilterText,
    columnFilters, setColumnFilter, clearColumnFilter,
    sortField, sortDir, applySort, pageSize, setPageSize, currentPage, setCurrentPage,
    fetchData, exportRows,
  } = list;

  const openNew = () => navigate('/customer-additional-fields/new');
  const openDetail = (row) => navigate(`/customer-additional-fields/${row.id}`, { state: { row } });
  // #282: kalem (Değiştir) düzenleme moduna açar
  const openEditRow = (row) => navigate(`/customer-additional-fields/${row.id}?edit=1`, { state: { row } });

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      const res = await fetch(`${API_BASE}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: deleteConfirm.id }) });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Kayıt silinemedi');
      showToast('success', 'Özelleştirilmiş alan başarıyla silindi');
      setDeleteConfirm(null);
      setSelected(null);
      fetchData();
    } catch (error) {
      showToast('error', error.message || 'Silme işlemi sırasında hata oluştu');
      setDeleteConfirm(null);
    }
  };

  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.accessor('textId', {
      header: 'Kod', meta: { filterType: 'text' },
      cell: (info) => { const v = info.getValue(); return v ? <span className="c360-erp-chip">{v}</span> : '-'; },
    }),
    columnHelper.accessor('name', { header: 'Ad', meta: { filterType: 'text' }, cell: (info) => info.getValue() || '-' }),
    columnHelper.accessor('dataType', {
      header: 'Veri Tipi', meta: { filterType: 'select' },
      cell: (info) => dataTypeLabel(info.getValue()),
    }),
    columnHelper.accessor('isRequired', {
      header: 'Zorunlu', meta: { filterType: 'select' },
      cell: (info) => (info.getValue() ? <StatusBadge tone="ok">Evet</StatusBadge> : <StatusBadge tone="neutral">Hayır</StatusBadge>),
    }),
    columnHelper.accessor('createdDate', {
      header: 'Oluşturma', enableColumnFilter: false,
      cell: (info) => { const v = info.getValue(); return v ? new Date(v).toLocaleDateString('tr-TR') : '-'; },
    }),
    columnHelper.accessor('updatedDate', {
      header: 'Güncelleme', enableColumnFilter: false,
      cell: (info) => { const v = info.getValue(); return v ? new Date(v).toLocaleDateString('tr-TR') : '-'; },
    }),
    columnHelper.display({
      id: 'actions', header: 'İşlemler', enableHiding: false,
      cell: ({ row }) => (
        <span className="c360-actions">
          <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--view" title="Görüntüle" aria-label="Görüntüle" onClick={(e) => { e.stopPropagation(); openDetail(row.original); }}><ViewIcon /></button>
          {canEdit && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--edit" title="Değiştir" aria-label="Değiştir" onClick={(e) => { e.stopPropagation(); openEditRow(row.original); }}><EditIcon /></button>}
          {canDelete && <button type="button" className="c360-action-btn c360-action-btn--icon c360-action-btn--delete" title="Sil" aria-label="Sil" onClick={(e) => { e.stopPropagation(); setSelected(row.original); setDeleteConfirm(row.original); }}><DeleteIcon /></button>}
        </span>
      ),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canEdit, canDelete]);

  const sorting = useMemo(() => (sortField ? [{ id: toGridSortField(sortField), desc: sortDir === 'desc' }] : []), [sortField, sortDir]);
  const handleSortingChange = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater;
    const first = next[0];
    if (!first) { applySort('TextId', 'asc'); return; }
    applySort(toODataSortField(first.id), first.desc ? 'desc' : 'asc');
  };
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility, pagination: { pageIndex: currentPage - 1, pageSize } },
    onSortingChange: handleSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    manualSorting: true,
    manualPagination: true,
    pageCount: totalPages,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    enableRowSelection: false,
    getRowId: (row) => String(row.id),
  });

  const handleExport = async () => {
    showToast('info', 'Dışa aktarılıyor...');
    try {
      const cols = [
        { field: 'textId', headerName: 'Kod' },
        { field: 'name', headerName: 'Ad' },
        { headerName: 'Veri Tipi', valueGetter: ({ row }) => dataTypeLabel(row.dataType) },
        { headerName: 'Zorunlu', valueGetter: ({ row }) => (row.isRequired ? 'Evet' : 'Hayır') },
      ];
      const dateStr = new Date().toISOString().slice(0, 10);
      const count = await exportRows(cols, `musteri-ozellestirilmis-alanlar_${dateStr}`);
      if (count === 0) showToast('error', 'Dışa aktarılacak veri yok');
      else showToast('success', `${count} kayıt dışa aktarıldı`);
    } catch (e) {
      showToast('error', e.message || 'Dışa aktarma başarısız');
    }
  };

  // === İçe aktarma ===
  const importPick = (row, keys) => { for (const k of keys) { if (row[k] != null && row[k] !== '') return String(row[k]).trim(); } return ''; };
  const parseDataType = (v) => {
    if (!v) return 1;
    const num = Number(v);
    if (Number.isFinite(num) && num >= 1 && num <= 4) return num;
    const found = DATA_TYPES.find((t) => t.label.toLowerCase() === String(v).toLowerCase());
    return found ? found.value : 1;
  };
  const parseBool = (v) => ['evet', 'true', '1', 'yes', 'x'].includes(String(v ?? '').trim().toLowerCase());
  const importRowToPayload = (row) => ({
    textId: importPick(row, ['Kod', 'textId', 'TextId']),
    name: importPick(row, ['Ad', 'Adı', 'name', 'Name']),
    dataType: parseDataType(importPick(row, ['Veri Tipi', 'dataType', 'DataType'])),
    isRequired: parseBool(importPick(row, ['Zorunlu', 'isRequired', 'IsRequired'])),
  });
  const importValidateRow = (p) => {
    if (!p.textId) return 'Kod boş';
    if (!p.name) return 'Ad boş';
    return null;
  };
  const importSubmitRow = async (p) => {
    try {
      const res = await fetch(`${API_BASE}/Add`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ textId: p.textId, name: p.name, dataType: p.dataType, isRequired: p.isRequired }) });
      if (res.ok) return { ok: true };
      return { ok: false, error: `Sunucu ${res.status}` };
    } catch (e) {
      return { ok: false, error: e.message || 'Network hatası' };
    }
  };
  const downloadImportTemplate = () => {
    exportToExcel([{ 'Kod': 'F001', 'Ad': 'Örnek Alan', 'Veri Tipi': 'Metin', 'Zorunlu': 'Hayır' }], null, 'musteri-ozellestirilmis-alan_sablon');
  };

  const toolbarNode = (
    <>
      <input
        style={s.filterInput}
        type="text"
        placeholder="Kod veya ad ile filtrele..."
        value={filterInput}
        onChange={(e) => setFilterInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') applyFilterText(filterInput); }}
      />
      {filterText && <button style={s.btnSecondary} onClick={() => { setFilterInput(''); applyFilterText(''); }}>Temizle</button>}
      <button style={s.btnSecondary} onClick={fetchData}>Yenile</button>
      <ColumnChooser table={table} />
      <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, paddingLeft: 12, borderLeft: '1px solid var(--border-color, rgba(15, 23, 42, 0.10))' }}>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel'e Aktar" aria-label="Excel'e Aktar" onClick={handleExport}><ExcelIcon size={16} /></button>
        <button type="button" style={{ ...s.btnSecondary, width: 38, height: 38, padding: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Excel/CSV'den İçe Aktar" aria-label="Excel/CSV'den İçe Aktar" onClick={() => setImportOpen(true)}><UploadIcon size={16} /></button>
      </span>
    </>
  );

  const footerNode = (
    <Pagination
      currentPage={currentPage}
      totalCount={totalCount}
      pageSize={pageSize}
      rowCount={rows.length}
      onPageChange={setCurrentPage}
      onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
    />
  );

  return (
    <div style={s.container}>
      <ListPageShell
        title="Müşteri Özelleştirilmiş Alan"
        subtitle="Müşteriler için özel alan tanımlarını oluşturun, düzenleyin ve yönetin."
        actions={canAdd && <button style={newRecordButtonStyle} onClick={openNew}>+ Yeni Özelleştirilmiş Alan</button>}
        toolbar={toolbarNode}
        footer={footerNode}
      >
        <C360Table
          table={table}
          columnFilterMeta={COLUMN_FILTER_META}
          columnFilters={columnFilters}
          onApplyFilter={setColumnFilter}
          onClearFilter={clearColumnFilter}
          loading={loading}
          selected={selected}
          onRowClick={setSelected}
          onRowDoubleClick={openDetail}
          emptyText="Kayıt bulunamadı"
        />
      </ListPageShell>

      {deleteConfirm && (
        <div style={s.overlay} onClick={() => setDeleteConfirm(null)}>
          <div style={{ ...s.modal, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}><h3 style={{ ...s.modalTitle, color: '#E53E3E' }}>Silme Onayı</h3></div>
            <div style={s.modalBody}><p style={{ margin: 0, color: 'var(--text-primary)', lineHeight: 1.6 }}><strong>"{deleteConfirm?.textId}"</strong> kodlu özelleştirilmiş alanı silmek istediğinize emin misiniz?</p></div>
            <div style={s.modalFooter}>
              <button style={s.btnDanger} onClick={handleDelete}>Evet, Sil</button>
              <button style={s.btnSecondary} onClick={() => setDeleteConfirm(null)}>İptal</button>
            </div>
          </div>
        </div>
      )}

      <ImportWizard
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onComplete={(okCount) => { if (okCount > 0) { showToast('success', `${okCount} alan içe aktarıldı`); fetchData(); } }}
        title="Özelleştirilmiş Alan İçe Aktar"
        helpText="Zorunlu alanlar: Kod ve Ad. Veri Tipi: Metin/Sayı/Tarih/Evet/Hayır (boş ise Metin). Zorunlu: Evet/Hayır."
        rowToPayload={importRowToPayload}
        validateRow={importValidateRow}
        submitRow={importSubmitRow}
        downloadTemplate={downloadImportTemplate}
      />
      <Toast toast={toast} />
    </div>
  );
}
